import { useState } from "react";
import { useTranslation } from "react-i18next";
import { openExternal } from "../../utils/openExternal";
import { copyText } from "../../utils/copyText";
import { Icon } from "../common/ui";

/**
 * The two latex practice sheets the student prints and works on top of.
 *
 * Two ways out, because Telegram's webview neither prints nor saves a PDF:
 * "open" hands the URL to the system browser, and "copy link" covers the
 * clients where that hand-off is blocked or where the student wants the sheet
 * on a machine that actually has a printer attached.
 *
 * Rendered twice — a card on the lessons list and a compact strip inside every
 * lesson — because a sheet you only find once, before you start, is a sheet you
 * do not have when you sit down to practise.
 */

// Absolute, and deliberately a constant: `openExternal` hands the address to
// another app, and the copy button puts it into a message the student may open
// on a different device, so a same-origin relative path would break both.
// Point this at the CDN bucket when the sheets move off the app server.
const TEMPLATE_BASE = "https://beeapp.atlases.eu/kb/materials";

const TEMPLATES = [
  { key: "blocks", file: "latex-1-blocks.pdf" },
  { key: "brows", file: "latex-2-brows.pdf" },
] as const;

const urlFor = (file: string) => `${TEMPLATE_BASE}/${file}`;

/** Open + copy-link pair for one sheet. */
function TemplateActions({ file, compact }: { file: string; compact?: boolean }) {
  const { t } = useTranslation();
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const url = urlFor(file);

  const copy = async () => {
    const ok = await copyText(url);
    setState(ok ? "copied" : "failed");
    // On failure the URL stays on screen, so the student can select it by hand.
    setTimeout(() => setState("idle"), ok ? 1800 : 12000);
  };

  return (
    <>
      <div className="row" style={{ gap: 8 }}>
        <button
          className={compact ? "btn btn-glass" : "btn btn-violet"}
          onClick={() => openExternal(url)}
          style={{ flex: 1, paddingBlock: 8, fontSize: "0.82rem" }}
        >
          <Icon name="printer" size={15} /> {t("study.templateOpen")}
        </button>
        <button
          className="btn btn-glass"
          onClick={copy}
          aria-label={t("study.templateCopy")}
          style={{ flex: "none", paddingInline: 12, paddingBlock: 8, fontSize: "0.82rem" }}
        >
          <Icon name={state === "copied" ? "check" : "copy"} size={15} />
          {state === "copied" ? t("study.templateCopied") : t("study.templateCopy")}
        </button>
      </div>

      {state === "failed" && (
        <div
          className="t-xs"
          style={{
            marginTop: 7, padding: "7px 9px", borderRadius: 8, background: "var(--glass-1)",
            wordBreak: "break-all", userSelect: "all", color: "var(--text-2)",
          }}
        >
          {url}
        </div>
      )}
    </>
  );
}

/** Full card — the lessons list. */
export default function PrintTemplates() {
  const { t } = useTranslation();
  return (
    <div className="glass glass-lg anim-rise" style={{ padding: 18, marginBottom: 18 }}>
      <div className="row" style={{ gap: 11, marginBottom: 12 }}>
        <span style={{
          display: "grid", placeItems: "center", width: 38, height: 38, borderRadius: 11,
          background: "var(--violet-tint)", color: "var(--violet-text)", flex: "none",
        }}>
          <Icon name="printer" size={19} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div className="t-h">{t("study.templates")}</div>
          <div className="t-xs muted-2" style={{ lineHeight: 1.35 }}>{t("study.templatesHint")}</div>
        </div>
      </div>

      <div className="stack" style={{ "--gap": "10px" } as React.CSSProperties}>
        {TEMPLATES.map(({ key, file }) => (
          <div key={key} className="glass glass-1" style={{ padding: "11px 13px", borderRadius: 12 }}>
            <div className="row" style={{ gap: 9, marginBottom: 9 }}>
              <Icon name="fileText" size={17} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="t-sm" style={{ fontWeight: 700, display: "block" }}>
                  {t(`study.template_${key}`)}
                </span>
                <span className="t-xs muted-2" style={{ display: "block" }}>
                  {t(`study.template_${key}_for`)}
                </span>
              </span>
            </div>
            <TemplateActions file={file} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Compact strip — inside a lesson, next to where the practice happens. */
export function PrintTemplatesRow() {
  const { t } = useTranslation();
  return (
    <div className="glass glass-1" style={{ padding: "11px 13px", borderRadius: 12, marginBottom: 16 }}>
      <div className="row" style={{ gap: 8, marginBottom: 9 }}>
        <Icon name="printer" size={15} />
        <span className="t-xs" style={{ fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          {t("study.templates")}
        </span>
      </div>
      <div className="stack" style={{ "--gap": "9px" } as React.CSSProperties}>
        {TEMPLATES.map(({ key, file }) => (
          <div key={key}>
            <div className="t-xs muted-2" style={{ marginBottom: 5 }}>{t(`study.template_${key}`)}</div>
            <TemplateActions file={file} compact />
          </div>
        ))}
      </div>
    </div>
  );
}
